import { createReducer } from '@reduxjs/toolkit';

const createActionType = (baseName: string) => ({
  BASE: baseName,
  SUCCESS: `${baseName}--success`,
  FAILED: `${baseName}--failed`,
});

const createAction = (baseName: string) => {
  const types = createActionType(baseName);
  return {
    types,
    base: (payload: any) => ({ type: types.BASE, payload }),
    success: (payload: any) => ({ type: types.SUCCESS, payload }),
    failed: (payload: any) => ({ type: types.FAILED, payload }),
  };
};

const getTypes = (module: string, actionKey: string) => createActionType(`${module}/${actionKey}`);

interface SliceProps {
  prefix: string;
  initialState: object;
  reducers: object;
  externalReducers?: object;
  selectors: any;
}

const createSlice = ({ prefix, initialState, reducers = {}, externalReducers = {}, selectors = {} }: SliceProps) => {
  if (!prefix) {
    throw new Error('`prefix` is a required option for createSlice');
  }

  const selectorsNames = Object.keys(selectors);

  const reducersByType = {} as any;
  const actionsToCreate = {} as any;
  const sliceSelectors = {} as any;

  selectorsNames.forEach((selectorName: string) => {
    const selector = selectors[selectorName];

    // Pass slice state as first argument, to simplify selecting values,
    // and the full state as additional arg.
    sliceSelectors[selectorName] = (state: any, ...args: any) => selector(state[prefix], state, ...args);
  });

  Object.entries(reducers).forEach(([reducerName, reducersObject]) => {
    const currentActionTypes = getTypes(prefix, reducerName);

    reducersByType[currentActionTypes.BASE] = reducersObject.base;

    if (reducersObject.failed) {
      reducersByType[currentActionTypes.FAILED] = reducersObject.failed;
    }

    if (reducersObject.success) {
      reducersByType[currentActionTypes.SUCCESS] = reducersObject.success;
    }

    actionsToCreate[reducerName] = createAction(currentActionTypes.BASE);
  });

  Object.entries(externalReducers).forEach(([actionName, reducerFunction]) => {
    reducersByType[actionName] = reducerFunction;
  });

  const reducer = createReducer(initialState, reducersByType);

  return {
    prefix,
    reducer,
    actions: actionsToCreate,
    selectors: sliceSelectors,
  };
};

export { createSlice };
